import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineArrowRight, HiOutlineX, HiCheckCircle } from 'react-icons/hi';

const services = [
  {
    title: "Product Designing",
    description: "I design and develop consumer products with creative ability and hands-on approach which satisfies the customers' and markets' needs and trends.",
    details: [
      "I develop the user interfaces.",
      "Web Page design and development.",
      "I create the UX element interactions.",
      "I position your company brand.",
      "Design systems for scalability."
    ]
  },
  {
    title: "UI/UX Designing",
    description: "Creating intuitive and visually appealing user interfaces with a focus on user experience. Designing interfaces that are both beautiful and functional.",
    details: [
      "I develop the user interfaces.",
      "Web Page design and development.",
      "I create the UX element interactions.",
      "I position your company brand.",
      "Create modern 3-D experiences."
    ]
  },
  {
    title: "Visual Designing",
    description: "I create visual concepts, by hand or using computer software, to communicate ideas that inspire, inform, and captivate consumers.",
    details: [
      "Brand identity and logos.",
      "Typography and layout design.",
      "Marketing and advertising visuals.",
      "Illustration and graphic art.",
      "Motion graphics design."
    ]
  },
  {
    title: "Web Development",
    description: "Building responsive, high-performance websites and web applications using the latest industry standards and frameworks.",
    details: [
      "Frontend development (React/Next).",
      "Backend development (Node/PHP).",
      "API integration and management.",
      "Performance optimization.",
      "SEO and accessibility."
    ]
  }
];

export default function Experience() {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <section id="experience" className="pt-8 pb-20 px-4 sm:px-6 lg:px-8 bg-transparent overflow-hidden relative">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-80 h-80 bg-[#4779c4]/5 rounded-full blur-[120px] opacity-10"></div>
        <div className="absolute bottom-1/4 -right-32 w-80 h-80 bg-[#4779c4]/5 rounded-full blur-[120px] opacity-10"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10">
          <p className="text-slate-400 text-xs tracking-widest uppercase mb-1">My Services</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white font-['Lora']">
            What I <span className="text-[#4779c4]">Offer</span>
          </h2>
        </div>

        {/* Services Grid - 2x2 layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#2c456b]/35 backdrop-blur-xl border border-white/10 rounded-[2.2rem] p-7 flex flex-col justify-between hover:bg-[#2c456b]/50 hover:border-[#4779c4]/40 transition-all duration-300 group shadow-2xl relative overflow-hidden"
            >
              <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#4779c4]/10 rounded-full blur-[40px] pointer-events-none group-hover:bg-[#4779c4]/20 transition-all duration-500" />
              <h3 className="text-xl font-bold text-white mb-10 max-w-[130px] leading-tight font-['Lora'] group-hover:text-[#4779c4] transition-colors">
                {service.title.split(' ').map((word, i) => (
                  <span key={i} className="block">{word}</span>
                ))}
              </h3>

              <button
                onClick={() => setSelectedService(service)}
                className="flex items-center gap-2 text-[#4779c4] text-sm font-bold group-hover:gap-4 transition-all duration-300"
              >
                See More <HiOutlineArrowRight className="text-lg" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal / Backdrop */}
      <AnimatePresence>
        {selectedService && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedService(null)}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-xl bg-[#1b2d48] border border-white/10 rounded-[2.5rem] p-8 sm:p-12 shadow-2xl overflow-hidden"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedService(null)}
                className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors p-2"
              >
                <HiOutlineX className="text-2xl" />
              </button>

              <div className="text-center mb-8">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 font-['Lora']">
                  {selectedService.title}
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-sm mx-auto font-['Lora']">
                  {selectedService.description}
                </p>
              </div>

              <div className="space-y-4">
                {selectedService.details.map((detail, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <HiCheckCircle className="text-[#4779c4] text-xl flex-shrink-0 mt-0.5" />
                    <p className="text-white text-sm sm:text-base font-['Lora']">{detail}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}